/** APP_CONFIG_JSON control: show the generated app config.json keys and the published
 *  S3 link, and (re)publish it. Publishing runs the action (items.run) which uploads the
 *  config to the private app_configs bucket; the resulting URL comes back via detail().
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Fragment } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { formatValue } from '@/features/preflight/detailFormat';

import { copyOnlyActions, urlActions } from '../tenantLinks';

export function AppConfigJsonControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const configJson =
    item.detail.config_json && typeof item.detail.config_json === 'object'
      ? (item.detail.config_json as Record<string, unknown>)
      : {};
  const publishedUrl = typeof item.detail.published_config_url === 'string' ? item.detail.published_config_url : null;
  const publishedKey = typeof item.detail.published_config_key === 'string' ? item.detail.published_config_key : null;

  const publish = useMutation({
    mutationFn: () => items.run(topId, item.id),
    onSuccess: (res) => {
      const failed = !res.run || res.run.status === 'error';
      snackbar.show(
        failed ? res.run?.error_message || 'Publish failed.' : 'config.json published.',
        failed ? 'error' : 'success',
      );
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Publish failed.', 'error'),
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 1.5,
            rowGap: 0.25,
            fontFamily: 'monospace',
            fontSize: 12,
          }}
        >
          {Object.entries(configJson).map(([k, v]) => (
            <Fragment key={k}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                {k}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'inherit', wordBreak: 'break-all' }}>
                {formatValue(v)}
              </Typography>
            </Fragment>
          ))}
        </Box>

        {publishedUrl ? (
          <Typography variant="body2">
            <Link
              href={publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
              Open published config.json
            </Link>{' '}
            <ValueActions actions={urlActions(publishedUrl, 'Open published config.json')}>
              (copy URL)
            </ValueActions>
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not published yet.
          </Typography>
        )}
        {publishedKey && (
          <Typography variant="caption" color="text.secondary">
            Key:{' '}
            {/* The S3 key is what goes into an app build's config pointer. */}
            <ValueActions actions={copyOnlyActions(publishedKey, 'Copy config key')}>
              {publishedKey}
            </ValueActions>
          </Typography>
        )}

        {canManage && (
          <Box>
            <Button size="small" variant="contained" disabled={publish.isPending} onClick={() => publish.mutate()}>
              {publishedUrl ? 'Re-publish config.json' : 'Publish config.json'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
